import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("hr_user", JSON.stringify(data));
    } catch {
      setUser(false);
      localStorage.removeItem("hr_user");
      localStorage.removeItem("hr_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    if (data.access_token) localStorage.setItem("hr_token", data.access_token);
    const u = { ...data.user, password_change_required: data.password_change_required, password_change_reason: data.password_change_reason };
    setUser(u);
    localStorage.setItem("hr_user", JSON.stringify(u));
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    localStorage.removeItem("hr_token");
    localStorage.removeItem("hr_user");
    setUser(false);
  }, []);

  // Memoize the context value so consumers don't re-render on every Provider
  // render. Only changes when one of the actual dependencies changes.
  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
